/**
 * THE NUMBERS THE COPY STATES (spec §7 P-7.3 `DIGITS`, owner decision O-5).
 *
 * A limit, a retention period or a size that a sentence names ("Keep it under 200 characters",
 * "kept for 30 days") used to be typed into the string, in each language's digits: 52 Arabic
 * strings carried Arabic-Indic digits and 9 Latin ones, so a later switch of digit style was a
 * sweep, and a limit changed in code silently left the copy saying the old one. Now the string
 * names a placeholder and this map fills it, keyed by the message's full key, so a message is
 * filled the same way however a component reaches it (`t(key('NAME_LONG'))` included).
 *
 * `useWasloI18n`'s `t` merges these under the caller's own named values (the caller wins) and
 * writes every number through the formatter (`helpers/plural.js` `localisedArgs`). A value is
 * either:
 *   - a number: written as a number ("between {min} and {max}", "{size} MB");
 *   - `{ unit, count }`: the number WITH its noun, from the six-form phrase
 *     `WASLO.COMMON.COUNT.<unit>` ("٢٠٠ حرف", "٣٠ يومًا", "١٠ خيارات"), so the sentence around it
 *     needs no plural form of its own.
 *
 * Every number is the one the code enforces, imported where the code exports it. The rest are
 * facts this fork does not enforce itself; each says whose rule it is.
 */
import { LIMITS as MENU_LIMITS, MAX_OPTIONS } from './menu-draft.js';
import { LIMITS as TEMPLATE_LIMITS } from './template-draft.js';
import { MAX_CTA } from './task-arabic.js';
import {
  MAX_EXACT,
  MAX_INSTRUCTION,
  MAX_LINK_LABEL,
  MAX_NAME,
  MAX_OPTION_LABEL,
} from './task-flow-draft.js';
import {
  MAX_OPENING,
  MAX_START_WORD,
  MAX_TITLE,
  MAX_TRIGGER,
} from './task-compose.js';
import { MAX_CASE_VALUES } from './task-system-answer.js';
import { MAX_DESCRIPTION } from './connection-draft.js';

/** The phrase units (`WASLO.COMMON.COUNT.*`). */
export const COUNT_UNITS = Object.freeze([
  'DAYS',
  'HOURS',
  'MINUTES',
  'CHARACTERS',
  'OPTIONS',
  'VALUES',
  'TIMES',
]);

const chars = count => ({ unit: 'CHARACTERS', count });
const days = count => ({ unit: 'DAYS', count });
const hours = count => ({ unit: 'HOURS', count });

// The sidecar keeps a removed item and a revision for 30 days (`REVISION_RETENTION_DAYS`).
const KEPT_FOR = days(30);
// Meta: a paused template is paused for 3 hours, then 6, then disabled.
const PAUSES = { first: hours(3), second: hours(6) };
// Meta: an archived template is deleted 28 days after it was put away.
const ARCHIVE_DELETES_AFTER = days(28);
// Meta: a deleted template's name is locked, and its sends keep trying, for 30 days; the
// marketing time-to-live is 30 days; an approved template may be edited 10 times in 30 days.
const META_30_DAYS = days(30);
// The BFF's caps (`authoring-web/src/routes/api/config.ts`, `AddKnowledgeDialog.vue`).
const MAX_INSTRUCTIONS = 20000;
const MAX_SOURCE_NAME = 200;
const MAX_UPLOAD_MB = 10;
// The BFF's `MAX_PHOTO_BYTES` (`routes/api/documents.ts`, `AddKnowledgeDialog.vue`): 5 MB a photo.
const MAX_PHOTO_MB = 5;
// The sidecar's cap on an account's saved places (D1, P-4.2).
const MAX_PLACES = 20;

export const COPY_FACTS = Object.freeze({
  'WASLO.ALL_ANSWERS.REMOVED': { period: KEPT_FOR },
  'WASLO.PHOTOS.HINT': { size: MAX_PHOTO_MB },
  'WASLO.PHOTOS.TOO_BIG': { size: MAX_PHOTO_MB },
  'WASLO.TASKS.EDITOR.FLOW.PROBLEMS.LINK_LABEL_LONG': {
    limit: chars(MAX_LINK_LABEL),
  },
  'WASLO.TASKS.COMPOSE.PLACES.LIMIT': { count: MAX_PLACES },
  'WASLO.UNDO.HISTORY.DESCRIPTION': { period: KEPT_FOR },
  'WASLO.UNDO.REMOVED.DESCRIPTION': { period: KEPT_FOR },
  'WASLO.ANSWERS.SOURCE.REMOVE_BODY': { period: KEPT_FOR },
  'WASLO.MENUS.EDITOR.REMOVE.CONFIRM_BODY': { period: KEPT_FOR },
  'WASLO.TASKS.REMOVE.CONFIRM_BODY': { period: KEPT_FOR },
  'WASLO.SETTINGS.CONNECTIONS.EDITOR.REMOVE.CONFIRM_BODY': { period: KEPT_FOR },

  'WASLO.PERSONALITY.STYLE.ADVANCED_HINT': { min: 0, max: 2 },
  'WASLO.PERSONALITY.ERRORS.STYLE_RANGE': { min: 0, max: 2 },
  'WASLO.PERSONALITY.ERRORS.LONG_PROMPT': { limit: chars(MAX_INSTRUCTIONS) },

  'WASLO.ANSWERS.DIALOG.NAME_LONG': { limit: chars(MAX_SOURCE_NAME) },
  'WASLO.ANSWERS.DIALOG.FILE_LARGE': { size: MAX_UPLOAD_MB },
  'WASLO.ANSWERS.DIALOG.FILE_HINT': { size: MAX_UPLOAD_MB },

  'WASLO.TASKS.EDITOR.FLOW.PROBLEMS.NAME_LONG': { limit: chars(MAX_NAME) },
  'WASLO.TASKS.EDITOR.FLOW.PROBLEMS.INSTRUCTION_LONG': {
    limit: chars(MAX_INSTRUCTION),
  },
  'WASLO.TASKS.EDITOR.FLOW.PROBLEMS.LABEL_LONG': {
    limit: chars(MAX_OPTION_LABEL),
  },
  'WASLO.TASKS.EDITOR.FLOW.PROBLEMS.LABEL_LONG_DETAIL': {
    limit: chars(MAX_OPTION_LABEL),
  },
  'WASLO.TASKS.EDITOR.FLOW.PROBLEMS.EXACT_LONG': { limit: chars(MAX_EXACT) },
  'WASLO.TASKS.EDITOR.FLOW.PROBLEMS.TITLE_LONG': { limit: chars(MAX_TITLE) },
  'WASLO.TASKS.EDITOR.FLOW.PROBLEMS.TRIGGER_LONG': {
    limit: chars(MAX_TRIGGER),
  },
  'WASLO.TASKS.EDITOR.FLOW.PROBLEMS.CTA_LONG': { limit: chars(MAX_CTA) },
  'WASLO.TASKS.EDITOR.FLOW.PROBLEMS.START_WORD_LONG': {
    limit: chars(MAX_START_WORD),
  },
  'WASLO.TASKS.EDITOR.FLOW.PROBLEMS.OPENING_LONG': {
    limit: chars(MAX_OPENING),
  },
  'WASLO.TASKS.COMPOSE.JOB.WORD_LONG': { limit: chars(MAX_START_WORD) },
  'WASLO.TASKS.COMPOSE.WHATIF.SYSTEM.CASE_HINT': {
    limit: { unit: 'VALUES', count: MAX_CASE_VALUES },
  },

  'WASLO.MENUS.EDITOR.OPTIONS.DESCRIPTION': {
    limit: { unit: 'OPTIONS', count: MAX_OPTIONS },
  },
  'WASLO.MENUS.EDITOR.OPTIONS.FULL': {
    limit: { unit: 'OPTIONS', count: MAX_OPTIONS },
  },
  'WASLO.MENUS.EDITOR.ERRORS.OPTIONS_MANY': {
    limit: { unit: 'OPTIONS', count: MAX_OPTIONS },
  },
  'WASLO.MENUS.EDITOR.OPTIONS.BUTTONS_NOTE': {
    limit: chars(MENU_LIMITS.BUTTON_OPTION),
  },
  'WASLO.MENUS.EDITOR.OPTIONS.LIST_NOTE': { limit: chars(MENU_LIMITS.OPTION) },
  'WASLO.MENUS.EDITOR.MESSAGE.BUTTON_HINT': {
    limit: chars(MENU_LIMITS.BUTTON),
  },
  'WASLO.MENUS.EDITOR.ERRORS.TITLE_LONG': { limit: chars(MENU_LIMITS.TITLE) },
  'WASLO.MENUS.EDITOR.ERRORS.WHEN_LONG': { limit: chars(MENU_LIMITS.WHEN) },
  'WASLO.MENUS.EDITOR.ERRORS.BODY_LONG': { limit: chars(MENU_LIMITS.BODY) },
  'WASLO.MENUS.EDITOR.ERRORS.BUTTON_LONG': { limit: chars(MENU_LIMITS.BUTTON) },

  'WASLO.TEMPLATES.QUALITY_NOTE.LOW': PAUSES,
  'WASLO.TEMPLATES.LIFECYCLE.PAUSING': PAUSES,
  'WASLO.TEMPLATES.LIFECYCLE.PAUSED': PAUSES,
  'WASLO.TEMPLATES.LIFECYCLE.ARCHIVAL': { period: ARCHIVE_DELETES_AFTER },
  'WASLO.TEMPLATES.LIFECYCLE.ARCHIVED': { period: ARCHIVE_DELETES_AFTER },
  'WASLO.TEMPLATES.LIFECYCLE.PENDING_DELETION': { period: META_30_DAYS },
  'WASLO.TEMPLATES.REMOVE.NAME_LOCKED': { period: META_30_DAYS },
  'WASLO.TEMPLATES.REMOVE.IN_FLIGHT': { period: META_30_DAYS },
  'WASLO.TEMPLATES.EDITOR.EDIT_NOTE': {
    times: { unit: 'TIMES', count: 10 },
    period: META_30_DAYS,
  },
  'WASLO.TEMPLATES.EDITOR.WINDOW.MARKETING': { period: META_30_DAYS },
  'WASLO.TEMPLATES.EDITOR.MESSAGE.HEADER_HINT': {
    limit: chars(TEMPLATE_LIMITS.HEADER),
  },
  'WASLO.TEMPLATES.EDITOR.MESSAGE.BODY_HINT_NUMBERED': {
    limit: chars(TEMPLATE_LIMITS.BODY),
  },
  'WASLO.TEMPLATES.EDITOR.MESSAGE.BODY_HINT_NAMED': {
    limit: chars(TEMPLATE_LIMITS.BODY),
  },
  'WASLO.TEMPLATES.EDITOR.MESSAGE.FOOTER_HINT': {
    limit: chars(TEMPLATE_LIMITS.FOOTER),
  },
  'WASLO.TEMPLATES.EDITOR.BUTTONS.TEXT_HINT': {
    limit: chars(TEMPLATE_LIMITS.BUTTON_TEXT),
  },
  'WASLO.TEMPLATES.EDITOR.BUTTONS.CODE_HINT': {
    limit: chars(TEMPLATE_LIMITS.COPY_CODE),
  },
  'WASLO.TEMPLATES.EDITOR.AUTH.EXPIRY_HINT': {
    min: TEMPLATE_LIMITS.CODE_EXPIRY_MIN,
    max: TEMPLATE_LIMITS.CODE_EXPIRY_MAX,
  },
  'WASLO.TEMPLATES.EDITOR.ERRORS.BODY_LONG': {
    limit: chars(TEMPLATE_LIMITS.BODY),
  },
  'WASLO.TEMPLATES.EDITOR.ERRORS.HEADER_LONG': {
    limit: chars(TEMPLATE_LIMITS.HEADER),
  },
  'WASLO.TEMPLATES.EDITOR.ERRORS.FOOTER_LONG': {
    limit: chars(TEMPLATE_LIMITS.FOOTER),
  },
  'WASLO.TEMPLATES.EDITOR.ERRORS.EXPIRY_RANGE': {
    min: TEMPLATE_LIMITS.CODE_EXPIRY_MIN,
    max: { unit: 'MINUTES', count: TEMPLATE_LIMITS.CODE_EXPIRY_MAX },
  },
  'WASLO.TEMPLATES.EDITOR.ERRORS.BUTTON_TEXT_LONG': {
    limit: chars(TEMPLATE_LIMITS.BUTTON_TEXT),
  },
  'WASLO.TEMPLATES.EDITOR.ERRORS.BUTTON_CODE_LONG': {
    limit: chars(TEMPLATE_LIMITS.COPY_CODE),
  },
  'WASLO.TEMPLATES.EDITOR.ERRORS.WIRE_TEXT_TOO_LONG': {
    limit: chars(TEMPLATE_LIMITS.BUTTON_TEXT),
  },

  'WASLO.SETTINGS.CONNECTIONS.EDITOR.ERRORS.DESCRIPTION_LONG': {
    limit: chars(MAX_DESCRIPTION),
  },
});

export default COPY_FACTS;
